import { Inject } from '@odinjs/odin';
import { FormAPI, ModalService } from '@philips-internal/oswald-ui-components';
import { ActionAPI } from '@philips-internal/oswald-ui-components/lib/components';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnCancel, OnClick, OnEdit, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { HttpServiceError } from 'src/services/patient-http-service';
import { FragmentNames, MyChartPastHistoryPatientAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import './my-chart-past-history-patient-detail.css';

enum PastHistorySequence {
  NEW = 'new'
}

interface PastHistoryFormRecord extends Record<string, Value> {
  disease: string;
  endDate: Date;
  inactivationDate: Date;
  notes: string;
  releaseDate: Date;
  sequence: number;
  startDate: Date;
}

@Controller({  fragment: FragmentNames.MY_CHART_PAST_HISTORY_PATIENT })
class MyChartPastHistoryPatientDetailController extends BaseController {

  @Inject(MyChartPastHistoryPatientAliases.FORM_PAST_HISTORY_PATIENT)
    pastHistoryPatientFormApi!: FormAPI;

  @Inject(MyChartPastHistoryPatientAliases.ACTION_INACTIVATE)
    inactivateActionApi!: ActionAPI;

  @Inject(MyChartPastHistoryPatientAliases.ACTION_RELEASE)
    releaseActionApi!: ActionAPI;

  @Inject(MyChartPastHistoryPatientAliases.ACTION_DELETE)
    deleteActionApi!: ActionAPI;

  private getPastHistorySequence(): string {
    return this.getHistoryRouteParam<string>('pastHistorySequence');
  }

  @OnMount(MyChartPastHistoryPatientAliases.CONTEXTUAL_BAR_PAST_HISTORY_PATIENT)
  handleMount(): void {
    const typeAction = this.getHistoryRouteParam<string>('typeAction');
    if (typeAction !== 'new_past_history') {
      this.handleWhenIsDetailRoute(this.pastHistoryPatientFormApi);
    }
  }

  private handleWhenIsDetailRoute(pastHistoryPatientFormApi: FormAPI): void {
    pastHistoryPatientFormApi.setRecord({});
    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_PAST_HISTORY_PATIENT_DETAIL, { pastHistorySequence: this.getPastHistorySequence(), patientId: UserContext.patientId })
      .then(response => response.body as Record<string, Value>)
      .then(data => {
        data.disease = data.disease ? data.disease : '-';
        data.startDateDetail = data.startDate ? moment(data.startDate as string).format('L') : '';
        data.endDateDetail = data.endDate ? moment(data.endDate as string).format('L') : '';
        data.startDate = data.startDate ? moment(data.startDate as string).format(ISO_DATE_FORMAT) : '';
        data.endDate = data.endDate ? moment(data.endDate as string).format(ISO_DATE_FORMAT) : '';
        data.releaseDate = data.releaseDate ? moment(data.releaseDate as string).format(ISO_DATE_FORMAT) : '';
        data.inactivationDate = data.inactivationDate ? moment(data.inactivationDate as string).format(ISO_DATE_FORMAT) : '';
        data.notes = data.notes?.toString() || '-';

        pastHistoryPatientFormApi.setData(data);
        this.updateStateFormActions(false);
      });
  }

  private updateStateFormActions(isFormEditing: boolean): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_PAST_HISTORY_PATIENT_DETAIL, { pastHistorySequence: this.getPastHistorySequence(), patientId: UserContext.patientId })
      .then(response => response.body as Record<string, Value>)
      .then(response => {
        const hasReleaseDate = response.releaseDate != null;
        const hasinactivationDate = response.inactivationDate != null;
        this.setStateFormActions(isFormEditing, hasReleaseDate, hasinactivationDate);
      })
      .catch(({response}: HttpServiceError<string>) => this.showErrorNotification(response?.body as string));
  }

  private setStateFormActions(isFormEditing: boolean, hasReleaseDate: boolean, hasinactivationDate: boolean): void {
    this.pastHistoryPatientFormApi.setEditing(isFormEditing);
    this.pastHistoryPatientFormApi.setDisabled(hasReleaseDate || hasinactivationDate);
    this.inactivateActionApi.setEnabled(!isFormEditing && hasReleaseDate && !hasinactivationDate);
    this.releaseActionApi.setEnabled(!isFormEditing && !hasReleaseDate && !hasinactivationDate);
    this.deleteActionApi.setEnabled(!isFormEditing && !hasReleaseDate && !hasinactivationDate);
  }

  private showErrorNotification(messageServer: string):void {
    const message = messageServer || 'newaccount.error.internal.server.error.try.again';
    showToastNotificationError(message);
  }

  @OnEdit({ target: MyChartPastHistoryPatientAliases.FORM_PAST_HISTORY_PATIENT })
  handleEditForm() : void{
    this.updateStateFormActions(true);
  }

  @OnClick({ target: MyChartPastHistoryPatientAliases.ACTION_INACTIVATE })
  handleBtnInactivatePastHistory(): void {
    ModalService.openDialog({
      closeable: true,
      fragment: FragmentNames.MY_CHART_PAST_HISTORY_INACTIVATE,
      onSubmit: () => {
        this.handleRedirectPastHistory();
      },
      title: 'patient.label.justification'
    });
  }

  @OnClick({ target: MyChartPastHistoryPatientAliases.ACTION_RELEASE })
  handleBtnReleasePastHistory(): void {
    this.releasePastHistory(this.getPastHistorySequence());
    this.handleRedirectPastHistory();
  }

  @OnClick({ target: MyChartPastHistoryPatientAliases.ACTION_DELETE })
  handleBtnDeletePastHistory(): void {
    ModalService.openDialog({
      closeable: true,
      dialogDescription: 'patient.my-chart.delete-item',
      dockButtons: [
        {
          action: (): void => { close() },
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: (): void => {
            this.deletePastHistory(this.getPastHistorySequence());
          },
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }]
    });
  }

  private releasePastHistory(pastHistoryId: Value): void {
    const body = {
      id: pastHistoryId
    } as Record<string, Value>;

    HttpService.post(APPLICATION_ENDPOINTS.POST_MC_PAST_HISTORY_RELEASE, { patientId: UserContext.patientId }, { body })
      .then(() => showToastNotificationSuccess('patient.my-chart.released-success'))
      .catch(({ response }: HttpServiceError<string>) => this.showErrorNotification(response?.body as string));
  }

  private deletePastHistory(pastHistorySequence: Value): void {
    HttpService.delete(APPLICATION_ENDPOINTS.DELETE_MC_PAST_HISTORY, { pastHistorySequence, patientId: UserContext.patientId})
      .then(() => {
        this.handleRedirectPastHistory();
        showToastNotificationSuccess('patient.my-chart.delete-success');
      })
      .catch(({response}: HttpServiceError<string>) => {
        this.showErrorNotification(response?.body as string);
      });
  }

  @OnSubmit(MyChartPastHistoryPatientAliases.FORM_PAST_HISTORY_PATIENT)
  handleSubmit(event: SubmitEvent): void {
    const pastHistoryForm = event.record as PastHistoryFormRecord;
    const pastHistorySequence = this.historyService.getCurrentRouteParams().pastHistorySequence as string;
    const sequence = pastHistorySequence === PastHistorySequence.NEW ?  '' : pastHistorySequence;
    const disease = pastHistoryForm.disease ? pastHistoryForm.disease : '';
    const startDate = pastHistoryForm.startDate ? moment(pastHistoryForm.startDate, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT) : '';
    const endDate = pastHistoryForm.endDate ? moment(pastHistoryForm.endDate, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT) : '';
    const notes = pastHistoryForm.notes ? pastHistoryForm.notes : '';

    const body = {
      disease: disease,
      endDate: endDate,
      id: sequence,
      notes: notes,
      startDate: startDate
    } as Record<string, Value>;

    this.context.set({
      params: {
        ...body,
        ...pastHistoryForm,
      }
    });
    HttpService.put(APPLICATION_ENDPOINTS.POST_MC_PAST_HISTORY, { patientId: UserContext.patientId }, { body })
      .then(response => response.body as Record<string, Value>)
      .then(response => {
        this.handleReleaseAfterSubmit(response.sequence as number);
        this.handleRedirectPastHistory();
      })
      .catch(({response}: HttpServiceError<string>) => this.showErrorNotification(response?.body as string));
  }

  private handleReleaseAfterSubmit(pastHistoryId: number): void {
    ModalService.openDialog({
      closeable: true,
      dialogDescription: 'patient.my-chart.modal.wish-submit',
      dockButtons: [
        {
          action: (): void => { close() },
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: (): void => { this.releasePastHistory(pastHistoryId) },
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }
      ]
    });
  }

  @OnCancel({ target: MyChartPastHistoryPatientAliases.FORM_PAST_HISTORY_PATIENT })
  handleCancelButton(): void {
    this.handleRedirectPastHistory();
  }

  private handleRedirectPastHistory() : void {
    applicationContext.popNavigationItem();
  }
}
export default MyChartPastHistoryPatientDetailController;
